import React, { useState, useEffect } from 'react';
import { metaWhatsappPhoneNumberService } from '../../services/api';
import { ConversationChannel } from '../../utils/constants';

const MetaWhatsappPhoneNumberSection = () => {
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [filteredPhoneNumbers, setFilteredPhoneNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  // Form states
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null);
  const [formData, setFormData] = useState({
    waba_id: '',
    phone_number: '',
    name: '',
    channel: ConversationChannel.WHATSAPP_TWILIO
  });

  // Load phone numbers on component mount
  useEffect(() => {
    fetchPhoneNumbers();
  }, []);

  // Filter phone numbers when searchTerm or phoneNumbers change
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredPhoneNumbers(phoneNumbers);
    } else {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      const filtered = phoneNumbers.filter(
        (phoneNumber) =>
          (phoneNumber.id && phoneNumber.id.toString().includes(lowerCaseSearchTerm)) ||
          phoneNumber.name.toLowerCase().includes(lowerCaseSearchTerm) ||
          phoneNumber.phone_number.toLowerCase().includes(lowerCaseSearchTerm) ||
          (phoneNumber.waba_id && phoneNumber.waba_id.toLowerCase().includes(lowerCaseSearchTerm))
      );
      setFilteredPhoneNumbers(filtered);
    }
  }, [searchTerm, phoneNumbers]);

  // Fetch all phone numbers
  const fetchPhoneNumbers = async () => {
    setLoading(true);
    try {
      const data = await metaWhatsappPhoneNumberService.getAllPhoneNumbers();
      setPhoneNumbers(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching phone numbers:', err);
      setError('Failed to load phone numbers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle search input changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle dialog open/close
  const handleAddDialogOpen = () => {
    setFormData({
      waba_id: '',
      phone_number: '',
      name: '',
      channel: ConversationChannel.WHATSAPP_TWILIO
    });
    setOpenAddDialog(true);
  };

  const handleEditDialogOpen = (phoneNumber) => {
    setSelectedPhoneNumber(phoneNumber);
    setFormData({
      id: phoneNumber.id,
      waba_id: phoneNumber.waba_id,
      phone_number: phoneNumber.phone_number,
      name: phoneNumber.name,
      channel: phoneNumber.channel,
      is_active: phoneNumber.is_active
    });
    setOpenEditDialog(true);
  };

  const handleDeleteDialogOpen = (phoneNumber) => {
    setSelectedPhoneNumber(phoneNumber);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenAddDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    setSelectedPhoneNumber(null);
  };

  // CRUD operations
  const handleAddPhoneNumber = async () => {
    try {
      await metaWhatsappPhoneNumberService.createPhoneNumber(formData);
      handleCloseDialogs();
      fetchPhoneNumbers();
    } catch (err) {
      console.error('Error adding phone number:', err);
      setError('Failed to add phone number. Please try again.');
    }
  };

  const handleUpdatePhoneNumber = async () => {
    try {
      await metaWhatsappPhoneNumberService.updatePhoneNumber(formData.id, formData);
      handleCloseDialogs();
      fetchPhoneNumbers();
    } catch (err) {
      console.error('Error updating phone number:', err);
      setError('Failed to update phone number. Please try again.');
    }
  };

  const handleDeletePhoneNumber = async () => {
    try {
      await metaWhatsappPhoneNumberService.deletePhoneNumber(selectedPhoneNumber.id);
      handleCloseDialogs();
      fetchPhoneNumbers();
    } catch (err) {
      console.error('Error deleting phone number:', err);
      setError('Failed to delete phone number. Please try again.');
    }
  };

  // Get channel display name
  const getChannelName = (channelValue) => {
    const channel = Object.entries(ConversationChannel).find(([_, value]) => value === channelValue);
    return channel ? channel[0].replace('_', ' ') : channelValue;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Meta WhatsApp Phone Numbers</h2>
        <button
          className="bg-[#075e54] hover:bg-[#054c44] text-white px-4 py-2 rounded flex items-center space-x-2"
          onClick={handleAddDialogOpen}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          <span>Add Phone Number</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          <p>{error}</p>
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-4 relative">
        <div className="flex">
          <div className="relative flex-grow">
            <input
              type="text"
              placeholder="Search phone numbers by ID, name, phone number, or WABA ID..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full p-2 pl-10 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
            />
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 absolute left-3 top-2.5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
        </div>
        {searchTerm && (
          <div className="mt-2 text-sm text-gray-600">
            Found {filteredPhoneNumbers.length} {filteredPhoneNumbers.length === 1 ? 'phone number' : 'phone numbers'} matching "{searchTerm}"
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#075e54]"></div>
        </div>
      ) : filteredPhoneNumbers.length === 0 ? (
        <div className="bg-gray-50 p-8 text-center rounded">
          <p className="text-gray-500">
            {phoneNumbers.length === 0
              ? "No phone numbers found. Add one to get started."
              : "No phone numbers match your search. Try a different search term."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone Number</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">WABA ID</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Channel</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPhoneNumbers.map((phoneNumber) => (
                <tr key={phoneNumber.id}>
                  <td className="px-6 py-4 whitespace-nowrap">{phoneNumber.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{phoneNumber.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{phoneNumber.phone_number}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{phoneNumber.waba_id}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{getChannelName(phoneNumber.channel)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      phoneNumber.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {phoneNumber.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        className="text-indigo-600 hover:text-indigo-900"
                        onClick={() => handleEditDialogOpen(phoneNumber)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                      <button
                        className="text-red-600 hover:text-red-900"
                        onClick={() => handleDeleteDialogOpen(phoneNumber)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Dialog */}
      {openAddDialog && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Add New Phone Number</h3>
            <p className="text-gray-600 mb-4">Enter the details for the new Meta WhatsApp Phone Number.</p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                placeholder="Enter name"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <input
                type="text"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                placeholder="Enter phone number"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">WABA ID</label>
              <input
                type="text"
                name="waba_id"
                value={formData.waba_id}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                placeholder="Enter WABA ID"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Channel</label>
              <select
                name="channel"
                value={formData.channel}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
              >
                {Object.entries(ConversationChannel).map(([key, value]) => (
                  <option key={value} value={value}>
                    {key.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={handleCloseDialogs}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddPhoneNumber}
                className="px-4 py-2 bg-[#075e54] text-white rounded hover:bg-[#054c44]"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      {openEditDialog && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Edit Phone Number</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">ID</label>
              <input
                type="text"
                name="id"
                value={formData.id || selectedPhoneNumber.id}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <input
                type="text"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">WABA ID</label>
              <input
                type="text"
                name="waba_id"
                value={formData.waba_id}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Channel</label>
              <select
                name="channel"
                value={formData.channel}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
              >
                {Object.entries(ConversationChannel).map(([key, value]) => (
                  <option key={value} value={value}>
                    {key.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                name="is_active"
                value={formData.is_active}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
              >
                <option value={true}>Active</option>
                <option value={false}>Inactive</option>
              </select>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={handleCloseDialogs}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdatePhoneNumber}
                className="px-4 py-2 bg-[#075e54] text-white rounded hover:bg-[#054c44]"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      {openDeleteDialog && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Phone Number</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete the phone number "{selectedPhoneNumber?.name}"? This action cannot be undone.
            </p>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={handleCloseDialogs}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePhoneNumber}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetaWhatsappPhoneNumberSection;
